import { useState, useRef, useEffect, useId } from "react";
import "./ComboboxPopover.css";

interface ComboboxPopoverProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  transform?: (v: string) => string;
  maxSuggestions?: number;
  fullWidth?: boolean;
  /** Lands on the text input so a sibling `<label htmlFor>` can name it. */
  id?: string;
  /**
   * How the typed text is matched against `options`.
   *
   * `"prefix"` (the default) is a case-SENSITIVE `startsWith`, which is what every caller that
   * feeds this a fixed local list has always had.
   *
   * `"contains"` is a case-insensitive substring match, for a caller whose `options` were already
   * narrowed by a server. Those two filters have to agree: the trusted-contacts organization
   * filter searches with SQL `lower(...) LIKE '%term%'`, so the server happily returns
   * "Ohio State" for "state" — and the default prefix filter would then hide it again, leaving a
   * coach looking at an empty menu for a name that exists.
   */
  match?: "prefix" | "contains";
  /**
   * Offer the first `maxSuggestions` options when the field is still empty, instead of waiting for
   * something to be typed.
   *
   * Off by default, which is right for a field whose `options` are a long fixed list — fifty US
   * states dropping open the moment a form field takes focus is noise. On for a filter whose
   * options were fetched for this purpose: a coach who does not know what is in the directory has
   * nothing to type, and an empty menu tells them nothing about what they could pick.
   */
  suggestWhenEmpty?: boolean;
}

/**
 * A text field that suggests from a list — free text is always allowed, so this is a filter or a
 * field with hints, never a closed set of choices (that is `SelectPopover`).
 *
 * <p>It behaves like a dropdown as well as a search box, and it did not use to. The chevron was a
 * decorative SVG with `pointer-events: none`, so clicking the one thing on the control that looks
 * like a dropdown arrow did nothing at all; the menu opened only on focus or on a keystroke; there
 * was no way to clear a value short of selecting the text and deleting it; and the keyboard could
 * not reach the options, which for a listbox is not a rough edge but a control a keyboard user
 * cannot operate.</p>
 */
export const ComboboxPopover = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  transform,
  maxSuggestions = 8,
  fullWidth,
  id,
  match = "prefix",
  suggestWhenEmpty = false,
}: ComboboxPopoverProps) => {
  const [open, setOpen] = useState(false);
  /**
   * Which option the keyboard is on, as an index into `suggestions`, or -1 for none.
   *
   * Deliberately not "the selected option": the typed text is the value, and moving through the
   * list must not commit anything until Enter. Reset whenever the list changes underneath it, or
   * the highlight would point at a different row than it did a keystroke ago.
   */
  const [activeIndex, setActiveIndex] = useState(-1);
  /**
   * True when the menu was opened to BROWSE rather than to narrow — by the arrow, or by ArrowDown
   * on a field that already holds a value.
   *
   * Browsing shows everything, ignoring what is in the field. Without this, opening the arrow on a
   * committed value filtered the list down to that one value and the menu read as empty: there is
   * nothing to pick but the thing you already picked. Typing turns it off again.
   */
  const [browsing, setBrowsing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /**
   * Suppresses the next focus-triggered open.
   *
   * The toggle moves focus back to the input so the coach can carry on typing, and `onFocus`
   * opens the menu — so closing the menu with the arrow immediately reopened it. This lets the
   * toggle's own decision win over the focus it caused.
   */
  const suppressFocusOpenRef = useRef(false);
  const listId = `${useId()}-listbox`;

  const suggestions = browsing
    ? options.slice(0, maxSuggestions)
    : value
      ? options
          .filter((o) =>
            match === "contains"
              ? o.toLowerCase().includes(value.toLowerCase())
              : o.startsWith(value),
          )
          .slice(0, maxSuggestions)
      : suggestWhenEmpty
        ? options.slice(0, maxSuggestions)
        : [];

  const showMenu = open && suggestions.length > 0;

  // The highlight indexes into a list that changes as the coach types, so it cannot survive a
  // change to that list. Clamped rather than cleared, so typing one more character does not throw
  // away a highlight that is still in range.
  useEffect(() => {
    setActiveIndex((current) => (current >= suggestions.length ? -1 : current));
  }, [suggestions.length]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
    };
  }, [open]);

  const commit = (next: string) => {
    onChange(next);
    setOpen(false);
    setActiveIndex(-1);
    setBrowsing(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (transform) val = transform(val);
    onChange(val);
    setOpen(true);
    setActiveIndex(-1);
    // Typing — including backspacing — is narrowing, not browsing. The list widens back out as
    // characters come off, which is the behaviour a search field is expected to have.
    setBrowsing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      // Opening on ArrowDown is the one thing every native select does, and it was the only way
      // in for a keyboard user before this.
      e.preventDefault();
      if (!open) {
        // ArrowDown on a field that already holds a value browses, for the same reason the arrow
        // does: filtering by a committed value leaves only that value to look at.
        setBrowsing(Boolean(value));
        setOpen(true);
        return;
      }
      if (suggestions.length === 0) return;
      const delta = e.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => {
        const next = current + delta;
        if (next < 0) return suggestions.length - 1;
        if (next >= suggestions.length) return 0;
        return next;
      });
      return;
    }

    if (e.key === "Enter") {
      // Only when a row is highlighted. Otherwise Enter belongs to whatever encloses this — a
      // form's submit, or a filter that is already applied as it is typed.
      if (showMenu && activeIndex >= 0) {
        e.preventDefault();
        commit(suggestions[activeIndex]);
      }
      return;
    }

    if (e.key === "Escape") {
      // Closes the menu, never clears the value: Escape inside a dialog is also that dialog's
      // dismiss, and a coach pressing it to shut a dropdown must not lose what they typed.
      if (open) {
        e.stopPropagation();
        setOpen(false);
        setActiveIndex(-1);
        setBrowsing(false);
      }
      return;
    }

    if (e.key === "Tab") {
      setOpen(false);
      setActiveIndex(-1);
      setBrowsing(false);
    }
  };

  return (
    <div ref={ref} className={`cbp${fullWidth ? " cbp--full-width" : ""}`}>
      <div className="cbp__wrap">
        <input
          id={id}
          ref={inputRef}
          className={`cbp__input${open ? " cbp__input--open" : ""}${
            value ? " cbp__input--clearable" : ""
          }`}
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suppressFocusOpenRef.current) {
              suppressFocusOpenRef.current = false;
              return;
            }
            if (value || suggestWhenEmpty) setOpen(true);
          }}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-expanded={showMenu}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            showMenu && activeIndex >= 0 ? `${listId}-option-${activeIndex}` : undefined
          }
        />

        {/* Only with something to clear. An always-present clear button on an empty field is a
            control that does nothing, sitting where the chevron ought to be. */}
        {value && !disabled && (
          <button
            type="button"
            className="cbp__clear"
            aria-label={`Clear ${placeholder}`}
            onClick={() => {
              commit("");
              inputRef.current?.focus();
            }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <path
                d="M2 2l6 6M8 2l-6 6"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}

        {/* A real button, not the decorative SVG this used to be. It had `pointer-events: none`,
            so clicking the one part of the control that looks like a dropdown arrow did nothing —
            which is the complaint that prompted rewriting this. */}
        <button
          type="button"
          className={`cbp__toggle${showMenu ? " cbp__toggle--open" : ""}`}
          aria-label={showMenu ? "Hide suggestions" : "Show suggestions"}
          tabIndex={-1}
          disabled={disabled}
          onClick={() => {
            const willOpen = !showMenu;
            setBrowsing(willOpen);
            // Only when closing. Opening wants the focus-open to be a no-op rather than a fight,
            // and `willOpen` has already decided the outcome either way.
            suppressFocusOpenRef.current = !willOpen;
            setOpen(willOpen);
            setActiveIndex(-1);
            inputRef.current?.focus();
          }}
        >
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
            <path
              d="M2 3.5l3.5 3.5 3.5-3.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {showMenu && (
        <div className="cbp__menu" role="listbox" id={listId}>
          {suggestions.map((opt, index) => (
            <div
              key={opt}
              id={`${listId}-option-${index}`}
              className={[
                "cbp__option",
                opt === value ? "cbp__option--selected" : "",
                index === activeIndex ? "cbp__option--active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              role="option"
              aria-selected={opt === value}
              /* mousedown, not click: the input's blur would otherwise close the menu before the
                 click landed on the row the pointer was over. */
              onMouseDown={() => commit(opt)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              {opt === value && (
                <svg className="cbp__check" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
